import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart, Check, Stethoscope, ShieldCheck, HeartHandshake,
  PhoneCall, Quote, ChevronDown, ExternalLink, ArrowRight, MessageCircle, User
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, getVisitorId } from "@/lib/api";

const LOGO = "https://customer-assets.emergentagent.com/job_telemedicine-plus-1/artifacts/si76u1vy_image.png";
const HERO_IMAGES = [
  "/images/image4.png",
  "/images/image5.png",
  "https://images.pexels.com/photos/5163935/pexels-photo-5163935.jpeg",
  "/images/image1.png",
  "/images/image3.png"

];
const DOCTOR_CONSULT = "https://images.pexels.com/photos/8376271/pexels-photo-8376271.jpeg";
const PLAN_REDIRECT_URL = "https://app.m-insure.in/#/login";

const PLANS = [
  {
    slug: "introductory", name: "Welcome Package",
    tagline: "Start your wellness journey",
    price: 99, duration_days: 100, badge: "Best for First-Timers",
    benefits: [
      "24×7 Audio & Video Doctor Consultations in 16 Languages", 
      "22+ Medical Specialties",
      "Up to 50% Lab & 25% Medicine Savings",
      "Digital Membership Card",
    ],
    highlighted: true,
  },
 {
  slug: "base",
  name: "Family Care Package",
  tagline: "Special family care is on its way",
  price: 499,
  duration_days: 100,
  badge: "Best Value",
  benefits: [
     "1-Year Family Healthcare Package",
  "Unlimited 24×7 Digital Healthcare",
  " Comprehensive Wellness for the Entire Family",
  "Exclusive Health Benefits & Savings"],
  highlighted: true,
}
  // {
  //   slug: "hospicash", name: "HospiCash",
  //   tagline: "Daily cash benefit on hospitalisation",
  //   price: 799, duration_months: 12, badge: "Available",
  //   benefits: [
  //     "₹1,000 / day hospital cash benefit",
  //     "Covers hospitalisation up to 30 days",
  //     "No medical test required",
  //     "Instant claim processing",
  //   ],
  //   highlighted: false,
  // },
  // {
  //   slug: "personal-accident", name: "Personal Accident",
  //   tagline: "Stay protected, anytime, anywhere",
  //   price: 999, duration_months: 12, badge: "Available",
  //   benefits: [
  //     "₹5 Lakh accidental cover",
  //     "Permanent disability benefit",
  //     "Worldwide coverage 24x7",
  //     "Family floater options",
  //   ],
  //   highlighted: false,
  // },
  // // {
  // //   slug: "cyber-shield", name: "Cyber Shield",
  // //   tagline: "Digital safety for the modern family",
  // //   price: 1295, duration_months: 12, badge: "Available",
  // //   benefits: [
  // //     "Cyber fraud financial protection",
  // //     "Identity theft cover",
  // //     "Online shopping fraud cover",
  // //     "Expert legal assistance",
  // //   ],
  // //   highlighted: false,
  // // },
];

const PARTNERS = [
  { name: "M-Swasth", url: "https://m-swasth.in", description: "Telemedicine & OPD network" },
];

const BENEFITS = [
  { icon: PhoneCall, title: "On-Call Telemedicine", body: "24×7 video & voice consultations with verified doctors across specialities." },
  { icon: Stethoscope, title: "Family OPD Access", body: "Cashless OPD access for parents, spouse and children at the network clinics." },
  { icon: ShieldCheck, title: "HospiCash + PA Cover", body: "Daily cash benefit on hospitalisation and personal accident cover." },
  { icon: HeartHandshake, title: "Lifestyle Wellness", body: "Curated discounts on pharmacy, lab tests, diagnostics and gyms across India." },
];

const TESTIMONIALS = [
  { name: "Priya Sharma", city: "Pune", quote: "I called a doctor at 11pm for my daughter's fever — the consultation was instant and so reassuring. HappiBandhu feels like family." },
  { name: "Ramesh Iyer", city: "Bengaluru", quote: "The Base Family plan paid for itself in the first month. Three OPD visits, zero out-of-pocket." },
  { name: "Anjali Verma", city: "Lucknow", quote: "Loved the HospiCash claim experience — money was in my account before I left the hospital." },
];

const FAQS = [
  { q: "Who is HappiBandhu for?", a: "Every Indian family — from young parents to seniors. Our plans start at just ₹99 for 3 months, making quality healthcare accessible to all." },
  { q: "How are doctors verified?", a: "All doctors are board-certified, verified by our plan issuers, and have minimum 5 years of clinical practice." },
  { q: "Is my family covered under one plan?", a: "Yes, the Base Family plan (₹499 / 12 months) covers your spouse, children and parents under one membership." },
  { q: "How do claims work for HospiCash?", a: "Submit a digital claim from your issuer dashboard within 30 days of discharge. Payouts typically credited within 48 hours." },
  { q: "Can I cancel my membership?", a: "Yes — cancellation policies are managed by the issuer that issued your membership." },
];


function LikeButton({ target, count, liked, onToggle, testid, size = "md" }) {
  const [pop, setPop] = useState(false);
  const click = () => { setPop(true); setTimeout(() => setPop(false), 450); onToggle(target); };
  const sz = size === "lg" ? "px-5 py-2.5 text-base" : "px-3 py-1.5 text-sm";
  return (
    <button
      type="button" data-testid={testid} onClick={click}
      className={`inline-flex items-center gap-2 rounded-full border ${liked ? "border-[color:var(--brand-magenta)] bg-pink-50 text-[color:var(--brand-magenta)]" : "border-slate-200 bg-white text-slate-600 hover:border-pink-200 hover:text-[color:var(--brand-magenta)]"} ${sz} font-medium transition-colors shadow-sm`}
    >
      <Heart className={`${size === "lg" ? "h-5 w-5" : "h-4 w-4"} ${liked ? "fill-[color:var(--brand-magenta)]" : ""} ${pop ? "heart-pop" : ""}`} />
      <span data-testid={`${testid}-count`}>{count}</span>
    </button>
  );
}


function PartnerDropdown({ size = "md", testid = "partner-dropdown" }) {
  const open = (url) => window.open(url, "_blank", "noopener,noreferrer");
  const sizeCls = size === "lg"
    ? "px-7 py-6 text-base rounded-full"
    : "px-5 py-2.5 text-sm rounded-full";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid={testid}
          className={`bg-[color:var(--brand-magenta)] hover:bg-[color:var(--brand-magenta-deep)] text-white font-semibold shadow-lg hover:shadow-xl transition-all ${sizeCls}`}
        >
          Browse Plans <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel>Available Plans</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {PARTNERS.map((p) => (
          <DropdownMenuItem
            key={p.name}
            data-testid={`partner-item-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => open(p.url)}
            className="cursor-pointer flex items-start gap-3 py-3"
          >
            <div className="flex-1">
              <div className="font-semibold text-[color:var(--brand-navy)]">{p.name}</div>
              <div className="text-xs text-slate-500">{p.description}</div>
            </div>
            <ExternalLink className="h-4 w-4 text-slate-400 mt-1" />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


export default function Landing() {
  

  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);
  const [siteLikes, setSiteLikes] = useState(0);
  const [likeCounts, setLikeCounts] = useState({});
  const [likedSet, setLikedSet] = useState(new Set());
  const visitorId = getVisitorId();
  useEffect(() => {const loadLikes = async () => {
    try {
      const [summary, mine] = await Promise.all([
        api.get("/likes/summary"),
        api.get(`/likes/me?visitor_id=${visitorId}`),
      ]);

      const counts = summary.data.counts || {};

      setSiteLikes(counts.site || 0);
      setLikeCounts(counts);
      setLikedSet(new Set(mine.data.liked));

    } catch (e) {
      setSiteLikes(96);

      setLikeCounts({
        site: 96,
        base: 74,
        introductory: 41,
        hospicash: 34,
        "personal-accident": 22,
        "cyber-shield": 13,
      });

      setLikedSet(new Set());
    }
  };

  loadLikes();
}, [visitorId]);

  const handleLike = async (target) => {
    const wasLiked = likedSet.has(target);
    const newSet = new Set(likedSet);
    if (wasLiked) newSet.delete(target); else newSet.add(target);
    setLikedSet(newSet);
    if (target === "site") setSiteLikes((s) => s + (wasLiked ? -1 : 1));
    else setLikeCounts((c) => ({ ...c, [target]: (c[target] || 0) + (wasLiked ? -1 : 1) }));
    try {
      const { data } = await api.post("/likes", { target, visitor_id: visitorId });
      if (target === "site") setSiteLikes(data.count);
      else setLikeCounts((c) => ({ ...c, [target]: data.count }));
      toast.success(data.liked ? "Thanks for the love! ❤" : "Like removed");
    } catch {
      toast.success("Demo mode: like interaction is simulated");
    }
  };

  const handlePlanRedirect = (price) => {
    if (price === 99 || price === 499) {
      window.location.assign(PLAN_REDIRECT_URL);
    }
  };

  return (
    <div id="top" className="min-h-screen bg-[color:var(--bg-cream)]">
      <Navbar />

        {/* HERO */}
      <section className="relative overflow-hidden bg-[color:var(--bg-cream)]">
      {/* Background Blobs */}
      <div className="absolute -top-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-pink-100/60 blur-3xl" />
      <div className="absolute top-40 right-0 h-[32rem] w-[32rem] rounded-full bg-indigo-100/50 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-14 items-center">

      {/* LEFT */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="lg:col-span-6"
      >

        {/* Small Label */}
        <div className="inline-flex items-center rounded-full border border-pink-100 bg-white/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--brand-magenta)] shadow-sm">
          Family Wellness • Everyday Benefits • Trusted Care
        </div>

        {/* Heading */}
        <h1 className="mt-7 font-display font-bold text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[color:var(--brand-navy)]">
          A trusted
          <br />
          <span className="brand-gradient-text">
            bandhu for every family.
          </span>
        </h1>

        {/* Description */}
        <p className="mt-6 max-w-lg text-lg leading-8 text-slate-600">
          HappiBandhu brings healthcare, preventive wellness, family benefits and everyday value together in one simple membership.
          <br />
          <span className="font-semibold text-[color:var(--brand-navy)]">
            Start with an introductory membership from just ₹99.
          </span>
        </p>

        {/* Buttons */}
        <div className="mt-8 flex flex-wrap items-center gap-5">

          <PartnerDropdown
            size="lg"
            testid="hero-plan-dropdown"
          />

          <a
            href="#plans"
            data-testid="hero-view-plans-btn"
            className="group inline-flex items-center font-semibold text-[color:var(--brand-navy)] transition-all"
          >
            View all plans
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>

        </div>

        {/* Feature Grid */}
        <div className="mt-12 grid grid-cols-2 gap-x-10 gap-y-6 max-w-xl">

          <div className="flex items-center gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50">
              <MessageCircle className="h-5 w-5 text-indigo-600" />
            </div>

            <div>
              <div className="font-semibold text-[color:var(--brand-navy)]">
                Talk to a Doctor
              </div>
              <div className="text-sm text-slate-500">
                Anytime, from home
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-pink-50">
              <Heart className="h-5 w-5 text-pink-500" />
            </div>

            <div>
              <div className="font-semibold text-[color:var(--brand-navy)]">
                Preventive Wellness
              </div>
              <div className="text-sm text-slate-500">
                Support before issues grow
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50">
              <User className="h-5 w-5 text-sky-600" />
            </div>

            <div>
              <div className="font-semibold text-[color:var(--brand-navy)]">
                For Every Stage
              </div>
              <div className="text-sm text-slate-500">
                Parents, children and seniors
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
            </div>

            <div>
              <div className="font-semibold text-[color:var(--brand-navy)]">
                Everyday Value
              </div>
              <div className="text-sm text-slate-500">
                Benefits that keep giving
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Badge */}
        <div className="mt-10">

          <div className="inline-flex items-center rounded-full bg-gradient-to-r from-[color:var(--brand-magenta)] via-fuchsia-600 to-indigo-600 px-7 py-3.5 shadow-xl">


            <span className="text-sm font-semibold text-white tracking-wide">
              Because every family deserves a trusted companion for healthier living.
            </span>

          </div>

        </div>

      </motion.div>

      {/* RIGHT */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.15 }}
        className="lg:col-span-6 relative"
      >

        <div className="relative overflow-hidden rounded-[36px] border border-white/70 bg-white p-3 h-[586px] shadow-[0_40px_80px_rgba(15,23,42,0.12)]">

          <AnimatePresence mode="wait">

            <motion.img
              key={currentImage}
              src={HERO_IMAGES[currentImage]}
              alt="Happy Family"

              initial={{
                opacity: 0,
                scale: 1.08,
              }}

              animate={{
                opacity: 1,
                scale: 1,
              }}

              exit={{
                opacity: 0,
                scale: 1.03,
              }}

              transition={{
                duration: 1.2,
                ease: "easeInOut",
              }}

              className="h-[560px] w-full rounded-[28px] object-cover absolute inset-0"
            />

          </AnimatePresence>

        </div>

        {/* Floating Card */}
        <div className="absolute -bottom-6 left-8 rounded-2xl bg-white/95 backdrop-blur-md p-5 shadow-xl border border-white">

          <div className="flex items-center gap-4">

            <img
              src={LOGO}
              alt=""
              className="h-14 w-14 rounded-xl"
            />

            <div>

              <div className="font-display text-lg font-bold text-[color:var(--brand-navy)]">
                HappiBandhu
              </div>

              <div className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--brand-magenta)]">
                Har Parivaar Ka Apna Bandhu
              </div>

            </div>

          </div>

        </div>

      </motion.div>

      </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-10 mb-14 items-end">
            <div className="lg:col-span-2">
              <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-magenta)] font-semibold mb-3">
                Why HappiBandhu
              </div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[color:var(--brand-navy)] tracking-tight">
                Wellness without worries.<br />Benefits that fit real family life.
              </h2>
            </div>
            <p className="text-slate-600 leading-relaxed">
              From preventive care and doctor access to everyday family benefits, HappiBandhu is designed to support the moments that matter most.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map(({ icon: Icon, title, body }, idx) => (
              <motion.div
                key={title} data-testid={`benefit-card-${idx}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                className="card-soft p-7"
              >
                <div className="h-12 w-12 rounded-xl brand-gradient flex items-center justify-center text-white mb-5">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-display font-semibold text-xl text-[color:var(--brand-navy)] mb-2">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANS */}
      <section id="plans" className="py-20 md:py-28 bg-[color:var(--bg-soft)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-magenta)] font-semibold mb-3">
              Available Plans
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[color:var(--brand-navy)] tracking-tight">
              Wellness that fits everyday life.
            </h2>
            <p className="mt-4 text-slate-600">
              Discover memberships designed to bring trusted healthcare support, wellness benefits and family value together in one place.
            </p>
            <div className="mt-6 flex justify-center">
              <PartnerDropdown size="md" testid="plans-partner-dropdown" />
            </div>
          </div>

          <div className="flex flex-wrap justify-center items-stretch gap-6">
            {PLANS.map((p, idx) => (
              <motion.div
                key={p.slug}
                data-testid={`plan-card-${p.slug}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: idx * 0.07 }}
                className={`w-full max-w-sm h-full min-h-[420px] relative rounded-3xl p-7 bg-white border flex flex-col ${p.highlighted ? "border-[color:var(--brand-magenta)] shadow-[0_30px_60px_-15px_rgba(233,30,99,0.35)] ring-1 ring-[color:var(--brand-magenta)]/30" : "border-slate-100 shadow-[0_8px_30px_rgb(0_0_0_/_0.04)]"}`}
              >
                {p.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[color:var(--brand-magenta)] hover:bg-[color:var(--brand-magenta)] text-white px-3 py-1 text-xs uppercase tracking-wider">
                      {p.badge}
                    </Badge>
                  </div>
                )}
                {!p.highlighted && p.badge && (
                  <Badge variant="outline" className="self-start mb-4 text-[color:var(--brand-navy)] border-slate-200">
                    {p.badge}
                  </Badge>
                )}

                <h3 className="font-display font-bold text-2xl text-[color:var(--brand-navy)]">{p.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{p.tagline}</p>

                <div className="my-6 flex min-h-20 items-center rounded-2xl bg-slate-50 px-4 py-4">
                  {p.price != null ? (
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-display font-bold text-[color:var(--brand-navy)]">₹{p.price}</span>
                      <span className="text-slate-500 text-sm">/ {p.duration_days} {p.duration_days === 1 ? "day" : "days"}</span>
                    </div>
                  ) : (
                    <div className="text-left">
                      <div className="text-xl font-semibold text-[color:var(--brand-navy)]">Coming soon</div>
                      <div className="text-sm text-slate-500">More details will be shared soon</div>
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-7 flex-1">
                  {p.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="h-4 w-4 text-[color:var(--brand-magenta)] mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto flex items-center justify-between gap-3">
                  {(p.price === 99 || p.price === 499) && (
                    <Button
                      type="button"
                      data-testid={`plan-redirect-${p.slug}`}
                      onClick={() => handlePlanRedirect(p.price)}
                      className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy)]/90 text-white"
                    >
                      Continue
                    </Button>
                  )}
                  <LikeButton
                    target={p.slug}
                    count={likeCounts[p.slug] || 0}
                    liked={likedSet.has(p.slug)}
                    onToggle={handleLike}
                    testid={`like-button-${p.slug}`}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TELEMED IMAGE STRIP */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white order-2 lg:order-1">
            <img src={DOCTOR_CONSULT} alt="Online doctor consultation" className="w-full h-[460px] object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-magenta)] font-semibold mb-3">
              The Care Promise
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[color:var(--brand-navy)] tracking-tight">
              A doctor in your pocket.<br />A trusted bandhu by your side.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Speak to a verified doctor in under 5 minutes. Access cashless OPD at
              partner clinics. Get hospital cash credited within 48 hours.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Average doctor connect time: under 5 minutes",
                "Multi-language support: English, Hindi & 6 regional",
                "Curated marketplace of trusted partners",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-slate-700">
                  <ShieldCheck className="h-5 w-5 text-[color:var(--brand-navy)] shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PLAN */}
      <section id="plan" className="py-20 bg-[color:var(--bg-soft)]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-magenta)] font-semibold mb-3">
            Our Plan Options
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[color:var(--brand-navy)] tracking-tight">
            Choose the membership that fits your family.
          </h2>
          <p className="mt-4 text-slate-600">
            HappiBandhu brings trusted healthcare support, everyday benefits and wellness value into one simple experience.
          </p>
          <div className="mt-8 flex justify-center">
            <PartnerDropdown size="lg" testid="plan-section-dropdown" />
          </div>

          <div className="mt-10 grid sm:grid-cols-2 gap-4 text-left">
            {PARTNERS.map((p) => (
              <a
                key={p.name}
                data-testid={`partner-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between p-5 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-[color:var(--brand-magenta)]/30 transition-all"
              >
                <div>
                  <div className="font-display font-bold text-lg text-[color:var(--brand-navy)]">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.description}</div>
                </div>
                <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-[color:var(--brand-magenta)] group-hover:translate-x-1 transition-all" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-magenta)] font-semibold mb-3">
              Loved by families
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[color:var(--brand-navy)] tracking-tight">
              Stories from our parivaar.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, idx) => (
              <div key={t.name} data-testid={`testimonial-${idx}`} className="card-soft p-7">
                <Quote className="h-6 w-6 text-[color:var(--brand-magenta)] mb-4" />
                <p className="text-slate-700 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <div className="font-semibold text-[color:var(--brand-navy)]">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.city}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 md:py-28 bg-[color:var(--bg-soft)]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-magenta)] font-semibold mb-3">
              Frequently Asked
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[color:var(--brand-navy)] tracking-tight">
              Everything you wanted to know.
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {FAQS.map((f, idx) => (
              <AccordionItem
                key={f.q} value={`item-${idx}`}
                data-testid={`faq-item-${idx}`}
                className="bg-white rounded-2xl border border-slate-100 px-6 shadow-sm"
              >
                <AccordionTrigger className="font-display font-semibold text-left text-[color:var(--brand-navy)] hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="relative rounded-[2rem] overflow-hidden brand-gradient p-12 md:p-16 text-white shadow-2xl">
            <div className="absolute inset-0 grain opacity-30" />
            <div className="relative grid md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <h3 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
                  Ready to welcome a trusted bandhu into your family?
                </h3>
                <p className="mt-3 text-white/85">
                  Explore a membership that brings wellness, benefits and support closer to home.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <PartnerDropdown size="lg" testid="cta-partner-dropdown" />
                <LikeButton target="site" count={siteLikes} liked={likedSet.has("site")}
                  onToggle={handleLike} testid="like-button-cta" size="lg" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
